import React, { useCallback, useMemo } from 'react';

import moment from 'moment';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuTypePropType } from 'ui-components/components/dropdown-menu/dropdown-menu-type';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { getTimes } from 'ui-components/components/time-picker/helpers';

import { messages } from './messages';
import { LocalDeliveryCustomPickupDateModal } from '../../../components/modals/local-delivery-custom-pickup-date-modal';
import { twelveHourUserSetting, twelveHourUserSettingsNoTimeZone } from '../../../constants/timezone-format';
import { useModal } from '../../../hooks/use-modal';
import { useUITimeFormatSetting } from '../../../hooks/use-user-setting';
import { useFulfillmentPlannerContext } from '../../context';

const currentTime = new Date();
const ASAP_PICKUP_TIME = {
  display: <FormattedMessage {...messages.ASAP} />,
  value: 'ASAP',
};
const CUSTOM_DATE_TIME = {
  display: <FormattedMessage {...messages.custom} />,
  value: 'customDateTime',
};

export const PickupDropdown = ({ id, dropdownMenuType }) => {
  const { open, close, isOpen } = useModal();
  const { pickup, onPickupChange } = useFulfillmentPlannerContext();
  const userTimeFormat = useUITimeFormatSetting();

  const times = useMemo(() => {
    const timeArr = [CUSTOM_DATE_TIME, ASAP_PICKUP_TIME];
    const timeFormatToUse = userTimeFormat === twelveHourUserSetting ? twelveHourUserSettingsNoTimeZone : userTimeFormat;
    getTimes({
      startTime: currentTime.getHours(),
      endTime: 24,
      minuteIncrementUnit: 15,
      timeFormat: timeFormatToUse,
      allowPastTimes: false,
    }).forEach(({ display, value }) =>
      timeArr.push({
        display: <FormattedMessage {...messages.dateToday} values={{ display }} />,
        value: moment(value, timeFormatToUse).toISOString(),
      }),
    );
    return timeArr;
  }, [userTimeFormat]);

  const selectedTimeDisplay = () => {
    if (!pickup) {
      return ASAP_PICKUP_TIME;
    }

    const selectedTime = times.find(({ value }) => Date.parse(value) === Date.parse(pickup));
    if (!selectedTime && pickup) {
      const pickupDate = new Date(pickup);
      return currentTime - pickupDate >= 0 ? ASAP_PICKUP_TIME : CUSTOM_DATE_TIME;
    }
    return selectedTime || ASAP_PICKUP_TIME;
  };

  const selectedTime = useMemo(selectedTimeDisplay, [pickup, times]);

  const setTime = useCallback(
    (isoTimeValue) => {
      if (isoTimeValue === ASAP_PICKUP_TIME.value) {
        onPickupChange(null);
        return;
      }
      onPickupChange(isoTimeValue);
    },
    [onPickupChange],
  );

  const handleOnClick = useCallback(
    ({ value }) => {
      if (value === CUSTOM_DATE_TIME.value) {
        open();
      } else {
        setTime(value);
      }
    },
    [open, setTime],
  );

  const memoizedDropdownItems = useMemo(
    () =>
      times.map((time) => (
        <DropdownMenuItem key={time.value} onClick={() => handleOnClick(time)} selected={selectedTime.value === time.value}>
          {time.display}
        </DropdownMenuItem>
      )),
    [handleOnClick, selectedTime.value, times],
  );

  return (
    <>
      <DropdownMenu dropdownMenuShouldHaveWidthOfButton dropdownMenuType={dropdownMenuType} fullWidth id={id} variant='form'>
        <DropdownMenuToggler>
          {selectedTime.value === CUSTOM_DATE_TIME.value ? moment(pickup).calendar() : selectedTime.display}
        </DropdownMenuToggler>
        {memoizedDropdownItems}
      </DropdownMenu>
      {isOpen && <LocalDeliveryCustomPickupDateModal onClose={close} onSave={setTime} />}
    </>
  );
};

PickupDropdown.propTypes = {
  id: PropTypes.string,
  dropdownMenuType: DropdownMenuTypePropType,
};
